import React, { useState, useEffect } from 'react';
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Stack,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import DetailDialog, {
  DetailSection,
  DetailFieldGrid,
  DetailHeaderPhotos,
  DetailPhotos,
  money,
  listFilterFieldSx,
  listTableHeadSx,
  listActionBtnSx,
} from '../common/DetailDialog';
import PhotoThumb from '../common/PhotoThumb';
import baseURL from '../../baseURL';
import { computeArticleTypeWeightTotals, formatWeightGrams3 } from '../../utils/pledgeArticleWeights';
import { DEFAULT_DHARA, formatDhara } from '../../constants/dhara';
import { pledgeDisplayPhotos } from '../../utils/pledgePhotoUrl';
import { interestTillDate, calendarDaysBetween, resolvePledgeDhara } from '../../utils/dharaInterest';
import { interestForSession, isBlackLogin, rateLabel } from '../../utils/blackInterest';
import { markPledgeTicketPrinted, clearTransferToA } from '../../utils/pledgeApi';
import TransferToADialog from './TransferToADialog';

function shownInterest(pledge) {
  if (!isBlackLogin()) return interestTillDate(pledge, new Date(), DEFAULT_DHARA);
  return interestForSession(pledge, {
    black: true,
    dhara: resolvePledgeDhara(pledge, DEFAULT_DHARA),
    actualDays: calendarDaysBetween(pledge.date, new Date()),
    startDate: pledge.date,
    endDate: new Date()
  });
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function pledgeSortValue(pledge, field) {
  if (field === 'customer.name') {
    return `${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim();
  }
  if (field === 'loanDetails.ticketNo') return String(pledge.loanDetails?.ticketNo || '');
  if (field === 'customer.code') return String(pledge.customer?.code || '');
  if (field === 'loanDetails.principalAmount') return Number(pledge.loanDetails?.principalAmount) || 0;
  if (field === 'date') {
    const t = new Date(pledge.date).getTime();
    return Number.isFinite(t) ? t : 0;
  }
  return '';
}

function comparePledges(a, b, field, order) {
  const aValue = pledgeSortValue(a, field);
  const bValue = pledgeSortValue(b, field);
  if (typeof aValue === 'string' || typeof bValue === 'string') {
    return order * String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
  }
  return order * (aValue - bValue);
}

const AllPledges = () => {
  const navigate = useNavigate();
  const [pledges, setPledges] = useState([]);
  const [filteredPledges, setFilteredPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('loanDetails.ticketNo');
  const [sortOrder, setSortOrder] = useState(1);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pledgeToDelete, setPledgeToDelete] = useState(null);
  const [masterArticles, setMasterArticles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferPledgeIds, setTransferPledgeIds] = useState([]);

  useEffect(() => {
    fetchPledges();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data)) setMasterArticles(data);
        else if (Array.isArray(data?.data)) setMasterArticles(data.data);
      } catch (e) {
      }
    };
    fetchArticles();
  }, []);

  const fetchPledges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/pledges`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Log the complete fetch data
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pledges');
      }

      if (data.success) {
        const rows = data.data || [];
        setPledges(rows);
        setFilteredPledges([...rows].sort((a, b) => comparePledges(a, b, 'loanDetails.ticketNo', 1)));
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
        setSortField('loanDetails.ticketNo');
        setSortOrder(1);
      } else {
        setPledges([]);
        setFilteredPledges([]);
      }
    } catch (error) {
      setPledges([]);
      setFilteredPledges([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    applyFilters({ searchTerm: term });
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
      applyFilters({ dateFrom: date });
    } else {
      setDateTo(date);
      applyFilters({ dateTo: date });
    }
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = (overrides = {}) => {
    const term = overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm;
    const fromValue = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const toValue = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    const field = overrides.sortField !== undefined ? overrides.sortField : sortField;
    const order = overrides.sortOrder !== undefined ? overrides.sortOrder : sortOrder;
    let filtered = [...pledges];

    const query = String(term || '').trim();
    if (query) {
      const searchRegex = new RegExp(escapeRegExp(query), 'i');
      const digitsOnly = /^\d+$/.test(query);
      filtered = filtered.filter((pledge) => {
        const ticket = String(pledge.loanDetails?.ticketNo || '');
        if (digitsOnly) return ticket.startsWith(query);
        return (
          searchRegex.test(pledge.customer?.firstName || '') ||
          searchRegex.test(pledge.customer?.lastName || '') ||
          searchRegex.test(pledge.customer?.code || '') ||
          searchRegex.test(ticket) ||
          searchRegex.test(pledge.customer?.mobileNo || '') ||
          searchRegex.test(pledge.customer?.village || '') ||
          searchRegex.test(String(pledge.loanDetails?.principalAmount || ''))
        );
      });
    }

    if (fromValue || toValue) {
      let fromDate = null;
      let toDate = null;

      if (fromValue) {
        fromDate = new Date(fromValue);
        fromDate.setHours(0, 0, 0, 0);
      }

      if (toValue) {
        toDate = new Date(toValue);
        toDate.setHours(23, 59, 59, 999);
      }

      filtered = filtered.filter(pledge => {
        if (!pledge.date) return false;
        const pledgeDate = new Date(pledge.date);
        pledgeDate.setHours(0, 0, 0, 0);

        if (fromDate && pledgeDate < fromDate) return false;
        if (toDate && pledgeDate > toDate) return false;
        return true;
      });
    }

    if (field && order) {
      filtered.sort((a, b) => comparePledges(a, b, field, order));
    }

    setFilteredPledges(filtered);
  };

  const handleSort = (field) => {
    const nextOrder = sortField === field ? (sortOrder === -1 ? 1 : -1) : field === 'loanDetails.ticketNo' ? 1 : -1;
    setSortField(field);
    setSortOrder(nextOrder);
    applyFilters({ sortField: field, sortOrder: nextOrder });
  };

  const sortMark = (field) =>
    sortField === field ? (sortOrder === -1 ? ' ▼' : ' ▲') : '';

  const handleViewDetails = async (pledge) => {
    setSelectedPledge(pledge);
    setOpenDetails(true);
    // Refresh full pledge so hydrated / latest photos appear in the eye modal
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledge._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.pledge) {
        setSelectedPledge(data.pledge);
        setPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
        setFilteredPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
      }
    } catch (e) {
    }
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleEdit = (pledge) => {
    navigate(`/pledge/create?edit=${pledge._id}`);
  };

  const handleDelete = (pledge) => {
    setPledgeToDelete(pledge);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pledgeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledgeToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh the pledges list
        fetchPledges();
        setDeleteConfirmOpen(false);
        setPledgeToDelete(null);
      } else {
        alert(data.message || 'Failed to delete pledge');
      }
    } catch (error) {
      alert('Error deleting pledge');
    }
  };

  const handlePrint = async (pledge) => {
    try {
      const { printDocument } = await import('../../utils/printUtility');
      const { PledgePrint } = await import('../../components/print/PrintTemplates');
      await printDocument('pledge', pledge, PledgePrint);
      await markPledgeTicketPrinted(pledge._id);
      const markPrinted = (p) =>
        p._id === pledge._id ? { ...p, ticketPrinted: true } : p;
      setPledges((prev) => prev.map(markPrinted));
      setFilteredPledges((prev) => prev.map(markPrinted));
    } catch (error) {
      alert(error.message || 'Error printing document. Please try again.');
    }
  };

  const transferableIds = (ids) =>
    ids.filter((id) => {
      const p = pledges.find((x) => x._id === id);
      return p && !p.transferredToA;
    });

  const toggleSelectPledge = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredPledges
      .filter((p) => !p.transferredToA)
      .map((p) => p._id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const applyTransferResults = (results = []) => {
    const byId = Object.fromEntries(
      results.filter((r) => r.success).map((r) => [String(r.pledgeId), r])
    );
    const patch = (p) => {
      const r = byId[String(p._id)];
      if (!r) return p;
      return {
        ...p,
        transferredToA: true,
        transferredAt: p.transferredAt || new Date().toISOString(),
        vpsATicketNo: r.vpsATicketNo,
        vpsAPledgeId: r.vpsAPledgeId,
      };
    };
    setPledges((prev) => prev.map(patch));
    setFilteredPledges((prev) => prev.map(patch));
    setSelectedIds((prev) => prev.filter((id) => !byId[String(id)]));
  };

  const openTransferDialog = (pledgeIds) => {
    const ids = transferableIds(pledgeIds);
    if (ids.length === 0) {
      alert('No pledges selected for transfer (already transferred pledges are skipped).');
      return;
    }
    setTransferPledgeIds(ids);
    setTransferDialogOpen(true);
  };

  const handleTransferSuccess = (results, { done } = {}) => {
    applyTransferResults(results);
    if (done) {
      fetchPledges();
      const failed = results.filter((r) => !r.success);
      if (failed.length) {
        alert(
          `Some transfers failed:\n${failed
            .map((f) => `${f.ticketNo || f.pledgeId}: ${f.error}`)
            .join('\n')}`
        );
      } else {
        alert('Transfer complete. Pledge remains on VPS B with "Transferred" status.');
      }
      setTransferDialogOpen(false);
      setTransferPledgeIds([]);
    }
  };

  const handleResetTransfer = async (pledge) => {
    if (!pledge?._id) return;
    const ok = window.confirm(
      `Reset transfer status for ticket ${pledge.loanDetails?.ticketNo || '—'}?\n\nUse this if the pledge was deleted on VPS A but still shows as transferred here.`
    );
    if (!ok) return;
    try {
      await clearTransferToA(pledge._id);
      await fetchPledges();
    } catch (err) {
      alert(err.message || 'Failed to reset transfer status');
    }
  };

  return (
    <>
      <GirviPageShell
        title="All Pledges"
        subtitle="Pledge · List"
        chipLabel="Pledge"
        maxWidth="xl"
       compact>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Name, code, ticket, mobile, village, amount"
                sx={listFilterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<TransferIcon />}
                disabled={selectedIds.length === 0}
                onClick={() => openTransferDialog(selectedIds)}
                sx={{ bgcolor: '#1e3a5f', fontWeight: 700 }}
              >
                Transfer selected ({selectedIds.length})
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default', width: 42 }}>
                    <Checkbox
                      size="small"
                      checked={
                        filteredPledges.filter((p) => !p.transferredToA).length > 0 &&
                        filteredPledges
                          .filter((p) => !p.transferredToA)
                          .every((p) => selectedIds.includes(p._id))
                      }
                      indeterminate={
                        filteredPledges.some((p) => selectedIds.includes(p._id)) &&
                        !filteredPledges
                          .filter((p) => !p.transferredToA)
                          .every((p) => selectedIds.includes(p._id))
                      }
                      onChange={toggleSelectAllVisible}
                    />
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('date')}>
                    Date{sortMark('date')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('loanDetails.ticketNo')}>
                    Ticket{sortMark('loanDetails.ticketNo')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx}>Photos</TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.code')}>
                    Code{sortMark('customer.code')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.name')}>
                    Customer{sortMark('customer.name')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('loanDetails.principalAmount')} align="right">
                    Amount{sortMark('loanDetails.principalAmount')}
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Interest till date
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }}>
                    Transfer
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPledges.length > 0 ? (
                  filteredPledges.map((pledge) => (
                    <TableRow
                      key={pledge._id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(pledge._id)}
                          disabled={!!pledge.transferredToA}
                          onChange={() => toggleSelectPledge(pledge._id)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
                        {formatDate(pledge.date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pledge.loanDetails?.ticketNo || 'N/A'}
                          sx={{
                            fontWeight: 800,
                            bgcolor: 'rgba(201,162,77,0.14)',
                            color: '#8a6d2f',
                            border: '1px solid rgba(201,162,77,0.35)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {(() => {
                            const photos = pledgeDisplayPhotos(pledge);
                            return (
                              <>
                                <PhotoThumb src={photos.clientPhoto} label="Client" size={36} />
                                <PhotoThumb src={photos.articlePhoto} label="Article" size={36} />
                              </>
                            );
                          })()}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {pledge.customer?.code || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim() || 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {money(pledge.loanDetails?.principalAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                        {money(shownInterest(pledge))}
                      </TableCell>
                      <TableCell>
                        {pledge.transferredToA ? (
                          <Chip
                            size="small"
                            label={pledge.vpsATicketNo ? `A: ${pledge.vpsATicketNo}` : 'Transferred'}
                            onDelete={() => handleResetTransfer(pledge)}
                            sx={{ fontWeight: 700 }}
                          />
                        ) : (
                          <Button size="small" onClick={() => openTransferDialog([pledge._id])}>
                            Transfer
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton onClick={() => handleViewDetails(pledge)} sx={listActionBtnSx} size="small">
                              <VisibilityIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleEdit(pledge)} sx={listActionBtnSx} size="small">
                              <EditIcon sx={{ fontSize: 18, color: '#15803d' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(pledge)} sx={listActionBtnSx} size="small">
                              <DeleteIcon sx={{ fontSize: 18, color: '#b91c1c' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print">
                            <IconButton onClick={() => handlePrint(pledge)} sx={listActionBtnSx} size="small">
                              <PrintIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No pledges found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GirviPageShell>

      <DetailDialog
        open={openDetails}
        onClose={handleCloseDetails}
        title={`Ticket ${selectedPledge?.loanDetails?.ticketNo || '—'}`}
        subtitle="Pledge details"
        status={selectedPledge?.status}
        statusColor={selectedPledge?.status === 'RELEASED' || selectedPledge?.status === 'closed' ? 'default' : 'success'}
        headerPhotos={
          (() => {
            const photos = pledgeDisplayPhotos(selectedPledge);
            return (
              <DetailHeaderPhotos
                clientPhoto={photos.clientPhoto}
                articlePhoto={photos.articlePhoto}
              />
            );
          })()
        }
      >
        {selectedPledge && (
          <>
            {(() => {
              const photos = pledgeDisplayPhotos(selectedPledge);
              return (
                <DetailPhotos
                  clientPhoto={photos.clientPhoto}
                  articlePhoto={photos.articlePhoto}
                  fingerprint={!!selectedPledge.fingerprint?.template || !!selectedPledge.fingerprint?.image}
                  size={110}
                />
              );
            })()}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailSection title="Customer">
                  <DetailFieldGrid
                    items={[
                      { label: 'Code', value: selectedPledge.customer?.code || 'N/A' },
                      {
                        label: 'Name',
                        value: `${selectedPledge.customer?.firstName || ''} ${selectedPledge.customer?.lastName || ''}`.trim() || 'N/A',
                      },
                      { label: 'Date', value: formatDate(selectedPledge.date) },
                    ]}
                  />
                </DetailSection>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailSection title="Loan">
                  <DetailFieldGrid
                    items={[
                      { label: 'Principal', value: money(selectedPledge.loanDetails?.principalAmount) },
                      { label: 'Interest till date', value: money(shownInterest(selectedPledge)) },
                      { label: 'Duration', value: `${selectedPledge.loanDetails?.duration || 'N/A'} months` },
                      { label: 'Rate', value: rateLabel(selectedPledge, isBlackLogin()) },
                      ...(selectedPledge.loanDetails?.dhara
                        ? [{ label: 'Dhara', value: formatDhara(selectedPledge.loanDetails.dhara) }]
                        : []),
                      { label: 'Paid excess', value: selectedPledge.loanDetails?.paidExcess ? 'Yes' : 'No' },
                    ]}
                  />
                </DetailSection>
              </Grid>
            </Grid>

            <DetailSection title="Articles">
              <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={listTableHeadSx}>Description</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Value</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Gross</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPledge.articles?.length ? selectedPledge.articles.map((article, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontWeight: 600 }}>{article.description || 'N/A'}</TableCell>
                        <TableCell align="right">{money(article.valueTotal)}</TableCell>
                        <TableCell align="right">{formatWeightGrams3(article.grossWeight || article.grossWt)} g</TableCell>
                        <TableCell align="right">{formatWeightGrams3(article.netWeight || article.netWt)} g</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                          No articles
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                {(() => {
                  const typeTotals = computeArticleTypeWeightTotals(selectedPledge.articles, masterArticles);
                  if (typeTotals.length >= 2) {
                    return typeTotals.map((t, idx) => (
                      <Typography key={idx} variant="body2" fontWeight={600}>
                        {t.title} gross: {formatWeightGrams3(t.totalGrossWeight)} g
                      </Typography>
                    ));
                  }
                  const gross = selectedPledge.totalGrossWeight
                    ? parseFloat(selectedPledge.totalGrossWeight)
                    : (selectedPledge.articles || []).reduce((s, a) => s + parseFloat(a.grossWeight || a.grossWt || 0), 0);
                  const net = selectedPledge.totalNetWeight
                    ? parseFloat(selectedPledge.totalNetWeight)
                    : (selectedPledge.articles || []).reduce((s, a) => s + parseFloat(a.netWeight || a.netWt || 0), 0);
                  return (
                    <>
                      <Typography variant="body2" fontWeight={600}>Total gross: {formatWeightGrams3(gross)} g</Typography>
                      <Typography variant="body2" fontWeight={600}>Total net: {formatWeightGrams3(net)} g</Typography>
                    </>
                  );
                })()}
              </Box>
            </DetailSection>
          </>
        )}
      </DetailDialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pledge? This action cannot be undone.
            {pledgeToDelete && (
              <Box sx={{ mt: 1.5 }}>
                <strong>Ticket No:</strong> {pledgeToDelete.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Customer:</strong> {pledgeToDelete.customer?.firstName || ''} {pledgeToDelete.customer?.lastName || ''}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <TransferToADialog
        open={transferDialogOpen}
        pledgeIds={transferPledgeIds}
        onClose={() => {
          setTransferDialogOpen(false);
          setTransferPledgeIds([]);
        }}
        onSuccess={handleTransferSuccess}
      />
    </>
  );
};

export default AllPledges;
